import React, { useEffect, useState } from 'react';

import { EmptyState } from '@/lib/base/empty-state';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { fetchWithDefaults } from '@/lib/http/client';
import { humanize } from '@/utils/text';

import { useProposalSidebar } from '../../contexts/ActionSidebarContext';
import type { DynamicView } from '../../types/dynamic_views';

interface Proposal {
  id: number;
  title: string;
  resolution: string;
}

const RESOLUTION_LABELS: Record<string, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Dismissed',
  failed: 'Failed',
  withdrawn: 'Withdrawn',
  executing: 'Executing',
};

interface MaterialsListProps {
  conversationId: number;
  orgId: string;
  onOpenDynamicViewSidebar: (viewId: number) => void;
  /** Key that changes to trigger a refetch (e.g., after proposal approval) */
  refetchKey?: number;
}

export const MaterialsList: React.FC<MaterialsListProps> = ({
  conversationId,
  orgId,
  onOpenDynamicViewSidebar,
  refetchKey,
}) => {
  const [views, setViews] = useState<DynamicView[]>([]);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const { openProposal } = useProposalSidebar();

  useEffect(() => {
    if (!orgId) {
      return;
    }

    const abortController = new AbortController();
    const { signal } = abortController;

    const loadMaterials = async () => {
      setLoading(true);
      try {
        // Load dynamic views and proposals in parallel
        const [viewsResponse, proposalsResponse] = await Promise.all([
          fetchWithDefaults(
            `/api/orgs/${orgId}/reasoning/conversations/${conversationId}/dynamic-views/`,
            {
              headers: {
                'Content-Type': 'application/json',
              },
              signal,
            }
          ),
          fetchWithDefaults(
            `/api/orgs/${orgId}/reasoning/conversations/${conversationId}/proposals/`,
            {
              headers: {
                'Content-Type': 'application/json',
              },
              signal,
            }
          ).catch(() => null), // Proposals endpoint may not exist yet
        ]);

        // Don't update state if request was aborted (conversation changed)
        if (signal.aborted) {
          return;
        }

        if (viewsResponse.ok) {
          const viewsData = await viewsResponse.json();
          setViews(viewsData.views || []);
        } else {
          // Clear views on fetch failure to avoid showing stale data from previous conversation
          setViews([]);
        }

        if (proposalsResponse?.ok) {
          const proposalsData = await proposalsResponse.json();
          setProposals(proposalsData.proposals || []);
        } else {
          // Clear proposals on fetch failure to avoid showing stale data from previous conversation
          setProposals([]);
        }
      } catch (error) {
        // Don't update state if request was aborted (conversation changed)
        if (signal.aborted) {
          return;
        }
        console.error('Failed to load materials:', error);
        // Clear views and proposals on error to avoid showing stale data
        setViews([]);
        setProposals([]);
      } finally {
        // Don't update loading state if request was aborted
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadMaterials();

    return () => {
      abortController.abort();
    };
  }, [conversationId, orgId, refetchKey]);

  const getViewTypeIcon = (viewType: string) => {
    switch (viewType) {
      case 'dashboard':
        return (
          <svg
            className='w-full h-full'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
            strokeWidth={1.5}
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z'
            />
          </svg>
        );
      case 'form':
        return (
          <svg
            className='w-full h-full'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
            strokeWidth={1.5}
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z'
            />
          </svg>
        );
      case 'report':
        return (
          <svg
            className='w-full h-full'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
            strokeWidth={1.5}
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z'
            />
          </svg>
        );
      default:
        return (
          <svg
            className='w-full h-full'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
            strokeWidth={1.5}
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z'
            />
          </svg>
        );
    }
  };

  const getResolutionStyles = (resolution: string) => {
    switch (resolution) {
      case 'approved':
        return 'bg-emerald-50 text-emerald-700';
      case 'rejected':
        return 'bg-red-50 text-red-700';
      case 'failed':
        return 'bg-rose-50 text-rose-700';
      case 'withdrawn':
        return 'bg-gray-50 text-gray-700';
      default: // pending
        return 'bg-blue-50 text-blue-700';
    }
  };

  if (loading) {
    return (
      <div className='flex flex-col justify-center items-center py-20'>
        <LoadingIndicator size='md' label='Loading materials…' />
      </div>
    );
  }

  // Show complete empty state only when there are NO materials at all
  if (views.length === 0 && proposals.length === 0) {
    return (
      <EmptyState
        variant='empty'
        title='No materials yet'
        description='Materials referenced in the conversation will appear here.'
        size='sm'
      />
    );
  }

  return (
    <div className='space-y-6 mt-[25px]'>
      {/* Proposals Section */}
      {proposals.length > 0 && (
        <div className='space-y-3'>
          <div className='flex items-center gap-2'>
            <h4 className='text-sm font-semibold text-fg-primary'>
              AI Actions
            </h4>
            <span className='inline-flex items-center px-2 py-0.5 bg-bg-secondary text-fg-primary rounded-full text-xs font-medium'>
              {proposals.length}
            </span>
          </div>

          <div className='border-t border-border-secondary'>
            {proposals.map(proposal => (
              <div
                key={proposal.id}
                className='flex items-center justify-between py-4 cursor-pointer hover:bg-bg-secondary/50 transition-colors border-b border-border-secondary last:border-b-0'
                onClick={() => openProposal(proposal.id)}
              >
                <div className='flex items-center gap-3 flex-1 min-w-0'>
                  {/* Icon */}
                  <div className='size-6 text-gray-500 flex-shrink-0'>
                    <svg
                      className='w-full h-full'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                      strokeWidth={1.5}
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M13 10V3L4 14h7v7l9-11h-7z'
                      />
                    </svg>
                  </div>

                  {/* Title */}
                  <p className='text-sm font-medium text-fg-primary truncate flex-1'>
                    {proposal.title}
                  </p>
                </div>

                <div className='flex items-center gap-4 flex-shrink-0'>
                  {/* Status badge */}
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getResolutionStyles(proposal.resolution)}`}
                  >
                    {RESOLUTION_LABELS[proposal.resolution] ??
                      humanize(proposal.resolution)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Views Section */}
      {views.length > 0 && (
        <div className='space-y-3'>
          <div className='flex items-center gap-2'>
            <h4 className='text-sm font-semibold text-fg-primary'>Views</h4>
            <span className='inline-flex items-center px-2 py-0.5 bg-bg-secondary text-fg-primary rounded-full text-xs font-medium'>
              {views.length}
            </span>
          </div>

          <div className='border-t border-border-secondary'>
            {views.map(view => (
              <div
                key={view.id}
                className='flex items-center justify-between py-4 cursor-pointer hover:bg-bg-secondary/50 transition-colors border-b border-border-secondary last:border-b-0'
                onClick={() => onOpenDynamicViewSidebar(view.id)}
              >
                <div className='flex items-center gap-3 flex-1 min-w-0'>
                  {/* Icon */}
                  <div className='size-6 text-gray-500 flex-shrink-0'>
                    {getViewTypeIcon(view.view_type_display.name)}
                  </div>

                  {/* Title */}
                  <p className='text-sm font-medium text-fg-primary truncate flex-1'>
                    {view.title}
                  </p>
                </div>

                <div className='flex items-center gap-4 flex-shrink-0'>
                  {/* Metadata */}
                  <div className='flex items-center gap-2 text-xs text-fg-quaternary'>
                    <span>{view.view_type_display.name}</span>
                    <span>·</span>
                    <span>v{view.version_number}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialsList;
